import { useState, useEffect, useRef, useCallback } from 'react';
import {
  defaultSalesAIConfig,
  generateSalesResponse,
  getProactiveMessage,
  SalesAIConfig,
} from '@/mocks/salesAIData';

interface Message {
  role: 'assistant' | 'user';
  content: string;
  timestamp: number;
}

export interface WizardContext {
  step: number;
  eventType: string;
  eventCity: string;
  coverageId: string | null;
  hasPreevento: boolean;
  extraIds: string[];
  finalTotal: number;
  cargoDistancia: number;
}

interface SalesAIChatProps {
  context: WizardContext;
  /**
   * Distancia desde el borde inferior, en píxeles.
   * En móvil se sube para no tapar la barra inferior fija ni los botones del flujo.
   * El valor por defecto conserva la posición original en escritorio.
   */
  bottomOffset?: number;
  /**
   * Contador externo para abrir el panel desde otro punto de la UI
   * (por ejemplo, el acceso "Asistente IA" de la hoja "Más" en móvil).
   * Cada cambio de valor abre el chat una vez.
   */
  openRequest?: number;
}

export default function SalesAIChat({ context, bottomOffset = 24, openRequest = 0 }: SalesAIChatProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [config] = useState<SalesAIConfig>(() => {
    try {
      const saved = localStorage.getItem('sales-ai-config');
      return saved ? { ...defaultSalesAIConfig, ...JSON.parse(saved) } : defaultSalesAIConfig;
    } catch {
      return defaultSalesAIConfig;
    }
  });
  const [hasSentInitial, setHasSentInitial] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const proactiveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevStepRef = useRef(context.step);
  const prevCoverageRef = useRef(context.coverageId);
  const prevExtraCountRef = useRef(context.extraIds.length);
  const prevOpenRequestRef = useRef(openRequest);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Apertura externa controlada (hoja "Más" en móvil, etc.)
  useEffect(() => {
    if (openRequest !== prevOpenRequestRef.current) {
      prevOpenRequestRef.current = openRequest;
      setIsOpen(true);
    }
  }, [openRequest]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 350);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !hasSentInitial && messages.length === 0) {
      setHasSentInitial(true);
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        setMessages([
          {
            role: 'assistant',
            content: config.mensajeInicial,
            timestamp: Date.now(),
          },
        ]);
      }, 800);
    }
  }, [isOpen, hasSentInitial, messages.length, config.mensajeInicial]);

  useEffect(() => {
    if (!isOpen || !hasSentInitial) return;

    const stepChanged = prevStepRef.current !== context.step;
    const coverageChanged = prevCoverageRef.current !== context.coverageId;
    const extraCountChanged = prevExtraCountRef.current !== context.extraIds.length;

    prevStepRef.current = context.step;
    prevCoverageRef.current = context.coverageId;
    prevExtraCountRef.current = context.extraIds.length;

    if (stepChanged || coverageChanged || extraCountChanged) {
      if (proactiveTimerRef.current) clearTimeout(proactiveTimerRef.current);
      proactiveTimerRef.current = setTimeout(() => {
        const msg = getProactiveMessage(context, config);
        if (msg) {
          setIsTyping(true);
          setTimeout(() => {
            setIsTyping(false);
            setMessages((prev) => [
              ...prev,
              { role: 'assistant', content: msg, timestamp: Date.now() },
            ]);
          }, 1000);
        }
      }, 1500);
    }

    return () => {
      if (proactiveTimerRef.current) clearTimeout(proactiveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.step, context.coverageId, context.extraIds.length, isOpen, hasSentInitial]);

  const handleSend = useCallback(() => {
    const text = inputValue.trim();
    if (!text) return;

    const userMsg: Message = { role: 'user', content: text, timestamp: Date.now() };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);

    const delay = 600 + Math.random() * 1200;
    setTimeout(() => {
      const response = generateSalesResponse(text, context, config);
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: response, timestamp: Date.now() },
      ]);
    }, delay);
  }, [inputValue, context, config]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestions = [
    '¿Qué incluye cada cobertura?',
    'Cuéntame del libro de firmas digital',
    '¿Cómo funciona el drone?',
    '¿Recomiendas algo para mi evento?',
  ];

  const handleSuggestion = (text: string) => {
    const userMsg: Message = { role: 'user', content: text, timestamp: Date.now() };
    setMessages((prev) => [...prev, userMsg]);
    setIsTyping(true);

    setTimeout(() => {
      const response = generateSalesResponse(text, context, config);
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: response, timestamp: Date.now() },
      ]);
    }, 800 + Math.random() * 800);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed z-50 transition-all duration-500 cursor-pointer group ${
          isOpen ? 'scale-0 opacity-0 pointer-events-none' : 'scale-100 opacity-100'
        }`}
        style={{ bottom: `${bottomOffset}px`, right: '24px' }}
        aria-label="Abrir chat con asesor"
      >
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-rose-400/30 animate-ping scale-150"></div>
          <div className="relative w-14 h-14 rounded-full bg-white border border-rose-200 flex items-center justify-center shadow-lg shadow-rose-200/30 hover:shadow-xl hover:shadow-rose-200/50 transition-all duration-500">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-rose-300">
              <img
                src={config.avatarUrl}
                alt={config.nombre}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white"></div>
          <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap shadow-md">
            IA
          </span>
        </div>
      </button>

      {/* Chat Panel */}
      <div
        className={`fixed z-50 w-[380px] max-w-[calc(100vw-32px)] h-[560px] bg-white border border-gray-200 rounded-2xl shadow-2xl shadow-rose-100/20 flex flex-col overflow-hidden transition-all duration-500 ease-out ${
          isOpen
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 translate-y-4 scale-95 pointer-events-none'
        }`}
        style={{
          bottom: `${bottomOffset}px`,
          right: '24px',
          maxHeight: `calc(100dvh - ${bottomOffset + 24}px)`,
        }}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-4 py-3.5 border-b border-gray-100 bg-gradient-to-r from-rose-50 to-white flex items-center gap-3">
          <div className="relative flex-shrink-0">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-rose-200">
              <img
                src={config.avatarUrl}
                alt={config.nombre}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white"></div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-gray-800 font-semibold text-sm">{config.nombre}</h3>
              <span className="text-[10px] font-bold bg-rose-100 text-rose-600 px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                IA
              </span>
            </div>
            <p className="text-emerald-600 text-xs flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
              En línea — Asesor de ventas
            </p>
          </div>

          <button
            onClick={() => setIsOpen(false)}
            className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-600 flex items-center justify-center transition-all duration-200 cursor-pointer"
            aria-label="Cerrar chat"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-close-line"></i>
            </div>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 custom-scrollbar">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-16 h-16 rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center mb-4">
                <i className="ri-customer-service-2-line text-2xl text-rose-400"></i>
              </div>
              <p className="text-gray-400 text-sm">
                ¿En qué puedo ayudarte con tu cotización?
              </p>
            </div>
          )}

          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fadeInUp`}
            >
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mr-2 mt-1 border border-rose-200">
                  <img
                    src={config.avatarUrl}
                    alt={config.nombre}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div
                className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-rose-500 text-white rounded-br-md'
                    : 'bg-gray-50 text-gray-700 border border-gray-100 rounded-bl-md'
                }`}
              >
                <p className="whitespace-pre-line">{msg.content}</p>
                <p className={`text-[10px] mt-1.5 ${msg.role === 'user' ? 'text-white/60' : 'text-gray-400'}`}>
                  {new Date(msg.timestamp).toLocaleTimeString('es-MX', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex justify-start animate-fadeInUp">
              <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mr-2 mt-1 border border-rose-200">
                <img
                  src={config.avatarUrl}
                  alt={config.nombre}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="bg-gray-50 border border-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-rose-400 rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 bg-rose-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                  <div className="w-1.5 h-1.5 bg-rose-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions */}
        {messages.length <= 1 && (
          <div className="flex-shrink-0 px-4 pb-2">
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestion(suggestion)}
                  className="text-xs text-gray-500 bg-gray-50 hover:bg-rose-50 hover:text-rose-500 border border-gray-100 hover:border-rose-200 rounded-full px-3 py-1.5 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="flex-shrink-0 p-3 border-t border-gray-100 bg-white">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe tu mensaje..."
              disabled={isTyping}
              className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-rose-300 focus:bg-white focus:ring-2 focus:ring-rose-100 transition-all duration-300 disabled:opacity-50"
            />
            <button
              onClick={handleSend}
              disabled={!inputValue.trim() || isTyping}
              className="w-10 h-10 rounded-xl bg-rose-500 text-white hover:bg-rose-600 flex items-center justify-center transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer flex-shrink-0 shadow-md shadow-rose-200/30"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-send-plane-fill"></i>
              </div>
            </button>
          </div>
          <p className="text-[10px] text-gray-300 text-center mt-2">
            Asesor virtual de Fotos y Video Santa Rosa
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.3s ease-out;
        }
        .custom-scrollbar::-webkit-scrollbar { width: 3px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(244,114,182,0.2); border-radius: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(244,114,182,0.35); }
      `}</style>
    </>
  );
}